// Game Recap sub-tab definitions.
//
// The Game Recap tab hosts one sub-tab per league. World Cup keeps its own
// component, route and table untouched; everything here describes only the new
// leagues, which share a single component (GameRecapTab) and a single prompt
// route (/api/gamerecap/run).
//
// `league` is doing double duty: it is the `sports` prop handed to the Remotion
// render AND the folder segment in the emblem URL the composition builds,
// `https://sportsgrid-labs.sportsgrid.com/logos/{league}/{ABBR}.svg`.

export type SportKey = 'nfl' | 'nba' | 'nhl' | 'wnba' | 'cfb';

// `category` values stored on image_videos rows. World Cup predates the Game
// Recap tab and keeps its original value.
export type RecapCategory = SportKey | 'worldcup';

// Every category that now lives under Game Recap — used to exclude these rows
// from the Instant Reaction tab, which shows only 'instant'.
export const RECAP_CATEGORIES: RecapCategory[] = ['nfl', 'nba', 'nhl', 'wnba', 'cfb', 'worldcup'];

export interface SportConfig {
  key: SportKey;
  /** Sub-tab label. */
  label: string;
  /** Full league name, used in prompt text. */
  fullName: string;
  /** The `sports` render prop + the logo folder segment. */
  league: string;
  /** What a scoring entry is called in this sport, for UI + prompt wording. */
  scorersLabel: string;
  /** Singular of the above, for the editor's placeholder and "add" button. */
  scorerNoun: string;
  /** What the `minute` field holds, e.g. game clock or a stat line. */
  clockLabel: string;
  /** Example `minute` values, handed to the model so the format is unambiguous. */
  clockExamples: string[];
  /** Prompt guidance for which players belong in `goalScorers`. */
  scorerRule: string;
  /**
   * CFB has hundreds of games a day, so its schedule is listed from Sportradar
   * and prompts run for one game at a time instead of the whole slate.
   */
  onDemand: boolean;
}

// Football fills the scorer slots with the three lines a football broadcast
// leads with instead of a scoring-play list — and for these two leagues the
// numbers are then overwritten from the Sportradar box score (see
// @/lib/sportradar), so this text is the fallback for a game Sportradar has no
// statistics for.
const FOOTBALL_LEADER_RULE =
  'A football game has too many scoring plays to list, so "goal_scorers" holds each side\'s ' +
  'GAME LEADERS instead: exactly three entries per side, in this order — the passing leader, ' +
  'the rushing leader, then the receiving leader, each by yards. The "minute" field is that ' +
  'player\'s stat line written as "<yards> PASS", "<yards> RUSH" or "<yards> REC" — no other ' +
  'words, because the scoreboard puts it on the same line as the name. Where one player leads ' +
  'two categories, give him ONE entry with both stats joined by a slash ("312 PASS/44 RUSH").';

export const SPORTS: Record<SportKey, SportConfig> = {
  nfl: {
    key: 'nfl',
    label: 'NFL',
    fullName: 'NFL',
    league: 'nfl',
    scorersLabel: 'Game leaders',
    scorerNoun: 'leader',
    clockLabel: 'stat line',
    clockExamples: ['312 PASS', '104 RUSH', '88 REC'],
    scorerRule: FOOTBALL_LEADER_RULE,
    onDemand: false,
  },
  nba: {
    key: 'nba',
    label: 'NBA',
    fullName: 'NBA',
    league: 'nba',
    scorersLabel: 'Leading scorers',
    scorerNoun: 'scorer',
    clockLabel: 'final point total',
    clockExamples: ['32 PTS', '28 PTS', '19 PTS'],
    scorerRule:
      'Basketball has far too many baskets to list individually, so "goal_scorers" holds each side\'s LEADING SCORERS instead — the 2-3 players who defined the game, with their final point totals. Never list individual baskets.',
    onDemand: false,
  },
  wnba: {
    key: 'wnba',
    label: 'WNBA',
    fullName: 'WNBA',
    league: 'wnba',
    scorersLabel: 'Leading scorers',
    scorerNoun: 'scorer',
    clockLabel: 'final point total',
    clockExamples: ['27 PTS', '21 PTS', '18 PTS'],
    scorerRule:
      'Basketball has far too many baskets to list individually, so "goal_scorers" holds each side\'s LEADING SCORERS instead — the 2-3 players who defined the game, with their final point totals. Never list individual baskets.',
    onDemand: false,
  },
  nhl: {
    key: 'nhl',
    label: 'NHL',
    fullName: 'NHL',
    league: 'nhl',
    scorersLabel: 'Goal scorers',
    scorerNoun: 'scorer',
    clockLabel: 'period + game clock',
    clockExamples: ["P1 12:04", "P3 18:47", "OT 2:31"],
    scorerRule:
      'List the goal scorers (not assists). Append " (PP)" for a power-play goal, " (SH)" short-handed, " (EN)" empty-net.',
    onDemand: false,
  },
  cfb: {
    key: 'cfb',
    label: 'CFB',
    fullName: 'NCAA College Football',
    league: 'cfb',
    scorersLabel: 'Game leaders',
    scorerNoun: 'leader',
    clockLabel: 'stat line',
    clockExamples: ['312 PASS', '104 RUSH', '88 REC'],
    scorerRule: FOOTBALL_LEADER_RULE,
    onDemand: true,
  },
};

export const SPORT_KEYS: SportKey[] = ['nfl', 'nba', 'nhl', 'wnba', 'cfb'];

export const isSportKey = (v: string): v is SportKey =>
  (SPORT_KEYS as string[]).includes(v);

/**
 * Emblem URL the Remotion composition will build for a team, mirroring
 * Scoreboard.tsx. Used by the UI to preview whether an abbreviation resolves.
 */
export const LOGO_CDN = 'https://sportsgrid-labs.sportsgrid.com/logos';

export const logoUrlFor = (league: string, abbr: string): string | null =>
  league && abbr ? `${LOGO_CDN}/${league.toLowerCase()}/${abbr.toUpperCase()}.svg` : null;

/**
 * Strip a status prefix off a model-supplied score so only the numbers reach
 * the scoreboard. The reaction prompt labels in-progress games "LIVE - 0-0",
 * and some finished ones come back as "FINAL 28-27" or "2-1 (AET)" — none of
 * which fit the template's two-number score band.
 *
 *   "LIVE - 0-0"   → "0-0"
 *   "FINAL 28-27"  → "28-27"
 *   "LIVE - 10-7"  → "10-7"
 *   "28-27"        → "28-27"
 *
 * A string with no number pair falls back to having the status words removed,
 * so nothing is silently blanked.
 */
export function cleanScore(raw: string | null | undefined): string {
  const s = (raw ?? '').trim();
  if (!s) return '';
  const m = s.match(/(\d+)\s*[-–—]\s*(\d+)/);
  if (m) return `${m[1]}-${m[2]}`;
  return s
    .replace(/\b(LIVE|FINAL|FT|HT|AET|OT)\b/gi, '')
    .replace(/^[\s\-–—:()]+|[\s\-–—:()]+$/g, '')
    .trim();
}
